import { Environment, getCurrentEnvironment } from '@/utils/environment';
import { getApiHostOverride, getDesignStudioLanguagesHostOverride } from '@/utils/environment-identity';

/**
 * Resolves the API base URL for the current account's region and environment.
 *
 * This is the single source of truth for "what host do I talk to?" —
 * used by `ts-rest.ts` for contract-based calls and `upload-asset.ts`
 * for multipart uploads. Previously each had its own inline URL map;
 * consolidating here prevents drift if hosts change.
 *
 * Reads region from `window.__hydra__` (set by the app shell before any
 * API call fires). Defaults to US when no account context is available
 * (e.g. login/auth flows).
 */
export function getApiBaseUrl(): string {
  const override = getApiHostOverride();
  if (override !== '') {
    return override;
  }

  const region = window.__hydra__?.account?.dc ?? 'us';

  if (__CIO_ENV__ === 'production') {
    return (
      {
        us: 'https://us.fly.customer.io',
        eu: 'https://eu.fly.customer.io',
      }[region] ?? 'https://us.fly.customer.io'
    );
  }

  if (__CIO_ENV__ === 'standalone') {
    return (
      {
        us: 'https://us.fly-standalone.customer.io',
        eu: 'https://us.fly-standalone.customer.io',
      }[region] ?? 'https://us.fly-standalone.customer.io'
    );
  }

  // Development — proxy through Vite / local dev server
  return '';
}

/**
 * The API base URL pinned to the US data center, regardless of the current
 * account's region. The SSO/SCIM configuration endpoints are served only from
 * the US data center — EU accounts that hit their own region time out
 * (opsbugs/4138) — so requests to them must always target the US host. In
 * development this resolves to the same empty string as {@link getApiBaseUrl}
 * (requests proxy through the dev server), so the pin is a no-op locally and in
 * tests.
 */
export function getUsApiBaseUrl(): string {
  const override = getApiHostOverride();
  if (override !== '') {
    return override;
  }

  if (__CIO_ENV__ === 'production') {
    return 'https://us.fly.customer.io';
  }

  if (__CIO_ENV__ === 'standalone') {
    return 'https://us.fly-standalone.customer.io';
  }

  return '';
}

/**
 * The base URL for the Design Studio procedures service, including its
 * `/design-studio/procedures` path prefix.
 *
 * Mirrors parcel's `proceduresUrl` map (`parcel/app/vite.config.ts`): US
 * production is deliberately the un-prefixed host (`fly.customer.io`) — unlike
 * the region-prefixed API hosts above — EU is `eu.fly.customer.io`, standalone
 * the un-prefixed standalone host. These are the exact URLs the embedded parcel
 * bundle calls from this same origin in production today (the endpoint is
 * unauthenticated; it also serves the public embeds), so routing and CORS are
 * already proven for hydra's origin. In development the same-origin path
 * proxies to local server-procedures (`vite.config.ts` maps
 * `/design-studio/procedures/` to localhost:6106).
 */
export function getDesignStudioProceduresBaseUrl(): string {
  const override = getApiHostOverride();
  if (override !== '') {
    return `${override}/design-studio/procedures`;
  }

  const region = window.__hydra__?.account?.dc ?? 'us';

  if (__CIO_ENV__ === 'production') {
    return region === 'eu'
      ? 'https://eu.fly.customer.io/design-studio/procedures'
      : 'https://fly.customer.io/design-studio/procedures';
  }

  if (__CIO_ENV__ === 'standalone') {
    return 'https://fly-standalone.customer.io/design-studio/procedures';
  }

  return '/design-studio/procedures';
}

/**
 * Every fly API host the current environment can use, US first then EU,
 * de-duplicated. Callers use it to tell whether a URL points at the fly API in
 * either region. Mirrors {@link getApiBaseUrl}: an API host override is the only
 * host (`[override]`), standalone maps both regions onto one host, and
 * development is `['']` (same-origin proxy).
 */
export function getAllApiBaseUrls(): string[] {
  const override = getApiHostOverride();
  if (override !== '') {
    return [override];
  }

  if (__CIO_ENV__ === 'production') {
    return ['https://us.fly.customer.io', 'https://eu.fly.customer.io'];
  }

  if (__CIO_ENV__ === 'standalone') {
    return ['https://us.fly-standalone.customer.io'];
  }

  return [''];
}

/**
 * The Design Studio language-server websocket host, with no trailing path.
 *
 * An environment with an API host override (preprod-dodo) has its own
 * subdomain, supplied at build time. If it supplies none this returns `''`
 * rather than falling through to staging or production, so the missing value
 * fails visibly instead of connecting an isolated environment to a shared host.
 */
export function getDesignStudioLanguagesBaseUrl(): string {
  if (getApiHostOverride() !== '') {
    return getDesignStudioLanguagesHostOverride();
  }

  const region = window.__hydra__?.account?.dc ?? 'us';

  // Classified by host like the rest of Design Studio (`getPreviewLoad`), not by `__CIO_ENV__`
  // alone: prod-staging-* builds resolve to production here and `devbox` to development.
  switch (getCurrentEnvironment()) {
    case Environment.PRODUCTION:
      return region === 'eu' ? 'wss://ds-languages-eu.fly.customer.io' : 'wss://ds-languages.fly.customer.io';
    case Environment.STANDALONE:
      return 'wss://ds-languages.fly-standalone.customer.io';
    case Environment.DEVELOPMENT:
      return 'ws://localhost:6103';
  }
}

/**
 * The Design Studio assets host, with no trailing slash. Production has its own
 * bucket; standalone, development and preprod-dodo share staging's (hydra's
 * deploy config has no dodo-specific Design Studio assets host). Matches what
 * `getPreviewLoad` hardcodes for the marshaled preview.
 */
export function getDesignStudioAssetsBaseUrl(): string {
  return getCurrentEnvironment() === Environment.PRODUCTION
    ? 'https://assets.parceldns.com'
    : 'https://assets.staging.parceldns.com';
}
